/**
 * Confirm "Mark as passed": closes the prospect out of the pipeline and cuts
 * preview access (links revoked, in-flight reservations expired). Reopenable
 * later from the row menu. The optional reason lands in the access-disable
 * audit trail.
 */
import { useRef, useState } from 'react';
import { LoDialogDescription, LoDialogShell, LoDialogTitle } from './LoRadixOverlay';
import type { LoProspect } from '@/types/lo-prospect';

export function PassDialog({
  prospect,
  busy,
  onClose,
  onSubmit
}: {
  prospect: LoProspect;
  busy?: boolean;
  onClose: () => void;
  onSubmit: (reason?: string) => void | Promise<void>;
}) {
  const [reason, setReason] = useState('');
  // Synchronous re-entry guard: the parent `busy` re-render lands too late to
  // stop a fast double-click. Resets in finally so a failed pass stays retryable.
  const submittingRef = useRef(false);
  const reasonRef = useRef<HTMLInputElement>(null);

  const submit = async () => {
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    try {
      await onSubmit(reason.trim() || undefined);
    } catch {
      // The parent surfaces errors (toast) and keeps the dialog open for retry.
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <LoDialogShell onClose={onClose} initialFocusRef={reasonRef}>
          <div className="modal-head">
            <div className="modal-eyebrow">Mark as passed</div>
            <LoDialogTitle>
              Pass on {prospect.firstName} {prospect.lastName}?
            </LoDialogTitle>
            <LoDialogDescription>
              Closes them out of the pipeline and locks them out of the preview portal — active invite
              links stop working. You can reopen them later from the row menu.
            </LoDialogDescription>
          </div>
          <div className="modal-body">
            <div className="field">
              <label>
                Reason <span className="hint">(optional — kept in the audit trail)</span>
              </label>
              <input
                ref={reasonRef}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. joined another brokerage"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void submit();
                  }
                }}
              />
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn-sm" onClick={onClose} type="button">
              Cancel
            </button>
            <button className="btn-sm danger" onClick={() => void submit()} disabled={busy} type="button">
              {busy ? 'Marking…' : 'Mark as passed'}
            </button>
          </div>
    </LoDialogShell>
  );
}
